import { cn } from "@/lib/utils";

interface TypographyProps extends React.HTMLAttributes<HTMLParagraphElement> {
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  variant?:
    | "h1Bold"
    | "h1Medium"
    | "h1Regular"
    | "h2Bold"
    | "h2Medium"
    | "h2Regular"
    | "h3ExtraBold"
    | "h3Bold"
    | "h3Medium"
    | "h3Regular"
    | "h4Bold"
    | "h4Medium"
    | "h4Regular"
    | "bodyLBold"
    | "bodyLMedium"
    | "bodyLRegular"
    | "bodyMBold"
    | "bodyMMedium"
    | "bodyMRegular"
    | "bodySBold"
    | "bodySMedium"
    | "bodySRegular"
    | "bodyESRegular"
    | "captionBold"
    | "captionMedium"
    | "captionRegular"
    | "captionSRegular"
    | "captionESMedium"
    | "error"
    | "success";
  color?:
    | "primary"
    | "secondary"
    | "muted"
    | "white"
    | "black"
    | "error"
    | "success"
    | "default"
    | "warning"
    | "info"
    | "black200"
    | "black300"
    | "gray400"
    | "gray500"
    | "gray600"
    | "gray900";
}

export const Typography: React.FC<TypographyProps> = ({
  as: Component = "p",
  variant = "bodyMRegular",
  color,
  className,
  children,
  ...props
}) => {
  const variants = {
    h1Bold: "text-8xl font-bold",
    h1Medium: "text-8xl font-medium",
    h1Regular: "text-8xl font-normal",
    h2Bold: "text-6xl font-bold",
    h2Medium: "text-6xl font-medium",
    h2Regular: "text-6xl font-normal",
    h3ExtraBold: "text-base md:text-4xl font-extrabold",
    h3Bold: "text-4xl font-bold",
    h3Medium: "text-4xl font-medium",
    h3Regular: "text-4xl font-normal",
    h4Bold: "text-2xl font-bold",
    h4Medium: "text-2xl font-medium",
    h4Regular: "text-2xl font-normal",
    bodyLBold: "text-xl font-bold",
    bodyLMedium: "text-xl font-medium",
    bodyLRegular: "text-xl font-normal",
    bodyMBold: "text-base font-bold",
    bodyMMedium: "text-base font-medium",
    bodyMRegular: "text-base font-normal",
    bodySBold: "text-sm font-bold",
    bodySMedium: "text-sm font-medium",
    bodySRegular: "text-sm font-normal",
    bodyESRegular: "text-[13px] font-normal",
    captionBold: "text-xs font-bold",
    captionMedium: "text-xs font-medium",
    captionRegular: "text-xs font-normal",
    captionSRegular: "text-[10px] font-normal",
    captionESMedium: "text-[8px] font-medium",
    error: "text-sm text-red-500",
    success: "text-sm text-green-500",
  };

  const colors = {
    primary: "text-primary",
    secondary: "text-secondary",
    muted: "text-muted-foreground",
    white: "text-white",
    black: "text-black",
    default: "text-foreground",
    error: "text-red-500",
    success: "text-green-500",
    warning: "text-yellow-500",
    info: "text-blue-500",
    black200: "text-black-200",
    black300: "text-black-300",
    gray400: "text-gray-400",
    gray500: "text-gray-500",
    gray600: "text-gray-600",
    gray900: "text-gray-900",
  };

  return (
    <Component
      className={cn(variants[variant], color && colors[color], className)}
      {...props}
    >
      {children}
    </Component>
  );
};
